import process from 'process';
import * as redux from 'redux';
// @ts-expect-error
import {reducer as formReducer} from 'redux-form/dist/redux-form';
import immutableStateInvariant from 'redux-immutable-state-invariant';
import {createEpicMiddleware} from 'redux-observable';
import {persistStore, persistReducer, persistCombineReducers, createTransform} from 'redux-persist';
// @ts-expect-error
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/hardSet';
import storage from 'redux-persist/lib/storage'; // or whatever storage you are using
import thunk from 'redux-thunk';

import {Accounts} from 'nutshell-core/accounts';
import {Billing} from 'nutshell-core/billing';
import * as Contacts from 'nutshell-core/contacts';
import {Session} from 'nutshell-core/session';
import type {SessionModuleState} from 'nutshell-core/session';
import {Store} from 'nutshell-core/store';
import type {NutshellSharedState} from 'nutshell-core/store';
import {Tasks} from 'nutshell-core/tasks';
import type {TasksModuleState} from 'nutshell-core/tasks';

import {App} from './app/index';
import {EntityList} from './entity-list';
import type {EntityListState} from './entity-list';
import {listenToLegacyEvents} from './legacy-event-listeners';
import {rootEpic} from './root-epic';
import {
    SIGNUP_REDUX_FORM,
    LOGIN_REDUX_FORM,
    SET_NEW_PASSWORD_REDUX_FORM,
} from './ui/auth/constants';
import {coreEntityPageReducer} from './ui/core-entity/redux/core-entity-page-reducer';
import type {CoreEntityPageState} from './ui/core-entity/redux/types';
import {emailComposerFormReducerPlugin} from './ui/email/sender/email-composer-form-reducer-plugin';
import {siteAnalyticsReducer} from './ui/email-marketing/analytics/report/analytics-reducer';
import type {SiteAnalyticsState} from './ui/email-marketing/analytics/report/analytics-reducer';
import {audiencesReducer} from './ui/email-marketing/audiences/audiences-reducer';
import type {AudiencesState} from './ui/email-marketing/audiences/audiences-reducer';
import {editionsReducer} from './ui/email-marketing/editions/editions-reducer';
import type {EditionsState} from './ui/email-marketing/editions/editions-reducer';
import {listSelectionReducer} from './ui/email-marketing/entity-lists/list-selection-redux/list-selection-reducer';
import type {PeopleListSelectionState} from './ui/email-marketing/entity-lists/list-selection-redux/list-selection-reducer';
import {mcfxCompaniesReducer} from './ui/email-marketing/visitors/companies-list/companies-reducer';
import type {McfxCompaniesState} from './ui/email-marketing/visitors/companies-list/companies-reducer';
import {webFormsReducer} from './ui/forms/forms-reducer';
import type {WebFormsState} from './ui/forms/forms-reducer';
import {landingPagesReducer} from './ui/landing-pages/report/pages-reducer';
import type {LandingPagesState} from './ui/landing-pages/report/pages-reducer';
import {dashboardReducer} from './ui/master-dashboard/dashboard-reducer';
import {reportsReducer} from './ui/reports/reports-reducer';
import type {ReportsState} from './ui/reports/reports-reducer';
import {aiAgentReducer} from './ui/team-ai/redux/agent-reducer';
import type {AiAgentState} from './ui/team-ai/redux/agent-reducer';
import {uiReducer} from './ui/ui-reducer';
import type {UiState} from './ui/ui-reducer';

type CollectionItemsState = {
    items: any[];
};

// Work in progress Nutshell State object, contains much more than what is
// listed here
export type NutshellState = NutshellSharedState &
    // @ts-expect-error
    App.AppModuleState &
    // @ts-expect-error
    Accounts.AccountsModuleState &
    // @ts-expect-error
    Billing.BillingModuleState &
    Contacts.ContactsModuleState &
    SessionModuleState &
    TasksModuleState & {
        audiences: AudiencesState;
        editions: EditionsState;
        peopleListSelection: PeopleListSelectionState;
        entityList: EntityListState;
        reports: ReportsState;
        users: CollectionItemsState;
        teams: CollectionItemsState;
        stagesets: CollectionItemsState;
        ui: UiState;
        form: any;
        webForms: WebFormsState;
        siteAnalytics: SiteAnalyticsState;
        mcfxCompanies: McfxCompaniesState;
        landingPages: LandingPagesState;
        aiAgent: AiAgentState;
        coreEntityPage: CoreEntityPageState;
    };

const IS_TEST_ENV = process.env.NODE_ENV === 'test';

// List of reducer keys that we purposefully we _not_ rehydrate
// with redux-persist i.e. on refresh all state goes away
const PERSIST_BLACKLISTED_REDUCERS = [
    'app',
    'activityTypes',
    'emailSequenceTemplateGroups',
    'audiences',
    'peopleListSelection',
    'competitors',
    'competitors',
    'editions',
    'entities',
    'form',
    'webForms',
    'landingPages',
    'leads',
    'listItems',
    'markets',
    'mcfxCompanies',
    'origins',
    'outcomes',
    'products',
    'reports',
    'routing',
    'salesAutomation',
    'siteAnalytics',
    'sources',
    'tags',
    'teams',
    'territories',
    'users',
    'visualPipeline',
    'aiAgent',
    'coreEntityPage',
];

export function configureStore() {
    const reducer = {
        [App.REDUCER_KEY]: App.reducer,
        [Accounts.REDUCER_KEY]: Accounts.reducer,
        audiences: audiencesReducer,
        peopleListSelection: listSelectionReducer,
        [Billing.REDUCER_KEY]: Billing.reducer,
        [Contacts.REDUCER_KEY]: Contacts.reducer,
        dashboard: dashboardReducer,
        editions: editionsReducer,
        entityList: EntityList.reducer,
        form: getReduxFormReducer(),
        [Session.REDUCER_KEY]: Session.reducer,
        [Tasks.REDUCER_KEY]: Tasks.reducer,
        reports: reportsReducer,
        ui: uiReducer,
        webForms: webFormsReducer,
        siteAnalytics: siteAnalyticsReducer,
        mcfxCompanies: mcfxCompaniesReducer,
        landingPages: landingPagesReducer,
        aiAgent: aiAgentReducer,
        coreEntityPage: coreEntityPageReducer,
    } as const;

    const epicMiddleware = createEpicMiddleware(rootEpic);
    const middleware = [thunk];
    if (typeof NutDebug !== 'undefined' && NutDebug.immutableStateInvariant) {
        // @ts-expect-error
        middleware.unshift(immutableStateInvariant());
    }

    // @ts-expect-error
    middleware.push(epicMiddleware);

    const enhancers = IS_TEST_ENV
        ? redux.applyMiddleware(...middleware)
        : redux.compose(redux.applyMiddleware(...middleware), reduxDevTools());

    // We need to pass a callback function to our shared store,
    // since we don't want to use the standard `combineReducers`
    // from redux, but instead the `peristCombineReducers` function
    // from `redux-persist`, which also uses a config
    // @ts-expect-error
    const combineFunction = (reducers) => {
        const persistConfig = {
            key: 'root',
            storage: storage,
            blacklist: PERSIST_BLACKLISTED_REDUCERS,
            // debug: true, // Uncomment this line for additional debugging!
        } as const;

        return persistCombineReducers(persistConfig, reducers);
    };

    return Store.configure(combineFunction, reducer, enhancers);
}

export const store = configureStore();
export const persistor = persistStore(store);

// We need some baseline session data set up upon initialization of our redux
// application, so we just immediately dispatch the fetch action after our store
// has been configured.
// But if we aren't logged in don't bother getting session data
const body = document.querySelector('body');
if (body && body.hasAttribute('data-logged-in')) {
    store.dispatch(Session.requestFetchSession());
}

// Start listening to legacy events
listenToLegacyEvents(store);

/**
 * Execute the redux devtools chrome extension if it exists and is safe to do so
 * (i.e. on development server)
 *
 * @return {function}  Middleware for devtools or noop
 */
function reduxDevTools() {
    // @ts-expect-error
    if (process.env.NODE_ENV !== 'production' && window.__REDUX_DEVTOOLS_EXTENSION__) {
        // @ts-expect-error
        return window.__REDUX_DEVTOOLS_EXTENSION__();
    }

    // @ts-expect-error
    return (f) => f;
}

function getReduxFormReducer() {
    const preventSavingFormsWithPasswords = createTransform(
        () => {
            // Don't allow anything to be written to localstorage at all.
            // Mostly this is to prevent passwords from being stored in localstorage, but it also
            // serves to avoid rehydrating form data, which we don't want either.
            return {};
        },
        // @ts-expect-error
        null,
        // define which reducers this transform gets called for.
        {whitelist: [SIGNUP_REDUX_FORM, LOGIN_REDUX_FORM, SET_NEW_PASSWORD_REDUX_FORM]}
    );

    const formPersistConfig = {
        key: 'form',
        storage: storage,
        stateReconciler: autoMergeLevel1,
        transforms: [preventSavingFormsWithPasswords],
        // debug: true, // Uncomment this line for additional debugging!
    } as const;

    // Special formReducer plugin here to do some
    // manipulation to our email composer state
    // when redux-form CHANGE actions occur
    const formReducerWithPlugins = formReducer.plugin({
        ...emailComposerFormReducerPlugin,
    });

    // @ts-expect-error
    return persistReducer(formPersistConfig, formReducerWithPlugins);
}
